import gradeService from "@/modules/ReportCard/Grades/service";
import termService from "@/modules/ReportCard/Terms/service";
import subjectService from "@/modules/ReportCard/Subject/service";
import curriculumService from "@/modules/ReportCard/Curriculums/service.js";
import termCommentService from "@/modules/ReportCard/TermComments/service";
import studentService from "@/modules/Student/service";

/**
 * Convert image URL → base64 (used by pdfMake)
 */
async function logoToBase64(imageUrl) {
  if (!imageUrl) return null;
  const response = await fetch(imageUrl, { mode: "cors" });
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

function formatOrdinal(num) {
  if (!num) return "N/A";
  const n = parseInt(num, 10);
  if ([11, 12, 13].includes(n % 100)) return `${n}th`;
  if (n % 10 === 1) return `${n}st`;
  if (n % 10 === 2) return `${n}nd`;
  if (n % 10 === 3) return `${n}rd`;
  return `${n}th`;
}

function getStudentId(student) {
  return student?.StudentId ?? student?.id ?? null;
}

function getStudentName(student) {
  return (
    student?.fullName ||
    student?.Student?.fullName ||
    student?.Student?.name ||
    student?.name ||
    "N/A"
  );
}

function getGradeLetter(percent) {
  if (percent >= 90) return "A";
  if (percent >= 80) return "B";
  if (percent >= 70) return "C";
  if (percent >= 60) return "D";
  return "F";
}

export async function buildStudentReportCard({
  student,
  classroom,
  year,
  currentBusiness,
}) {
  const currentStudentId = getStudentId(student);

  /* =========================
     1. Terms
  ========================= */
  const termsRes = await termService.getAll({ YearId: year.id });
  const terms = termsRes?.data || [];
  const lastTerm = terms[terms.length - 1];

  /* =========================
     2. Students
  ========================= */
  const studentsRes = await studentService.getStudentByClassRoomId({
    ClassRoomId: classroom.id,
  });
  const students = studentsRes?.data || [];
  const studentIds = students.map((s) => s.StudentId).filter(Boolean);

  /* =========================
     3. Curriculum & Subjects
  ========================= */
  let curriculum = null;
  if (classroom.CurriculumId) {
    const curriculumRes = await curriculumService.getById(classroom.CurriculumId);
    curriculum = curriculumRes?.data || null;
  }

  const subjectIds =
    curriculum?.subjectMeta?.subjects?.map((s) => s.id) || [];

  const subjectsRes = subjectIds.length
    ? await subjectService.getAll({ filteredSubjects: subjectIds })
    : { data: [] };

  const subjects = subjectsRes?.data || [];
  (curriculum?.subjectMeta?.subjects || []).forEach((meta) => {
    const s = subjects.find((x) => x.id === meta.id);
    if (s) s.weightage = meta.marks;
  });

  /* =========================
     4. Grades (ALL students)
  ========================= */
  const gradesRes = await gradeService.getAll({
    YearId: year.id,
    filteredStudents: studentIds,
    ClassroomId: classroom.id,
  });
  const allGrades = gradesRes?.data || [];
  const studentGrades = allGrades.filter(
    (g) => g.StudentId === currentStudentId
  );

  /* =========================
     5. Term Comments
  ========================= */
  const termCommentsRes = await termCommentService.getAll({
    filteredTerms: terms.map((t) => t.id),
    StudentId: currentStudentId,
  });

  const termCommentTable = (termCommentsRes?.data || []).map((row) => [
    { text: `${row.Term?.name || ""} Comments:`, bold: true },
    { text: row.comment || "" },
  ]);

  /* =========================
     6. Build Grade Table + Overall %
  ========================= */
  const reportCard = [];
  let overallPercentage = 0;

  const headerRow = [{ text: "Subject", bold: true }];
  const dateRow = [{ text: "Date", bold: true }];
  const labelRow = [{ text: "", bold: true }];

  terms.forEach((term) => {
    headerRow.push({ text: term.name, colSpan: 2, alignment: "center" });
    headerRow.push({});
    dateRow.push({
      text: new Date(term.createdAt).toLocaleDateString(),
      colSpan: 2,
      alignment: "center",
    });
    dateRow.push({});
    labelRow.push({ text: "Marks", alignment: "center" });
    labelRow.push({ text: "%", alignment: "center" });
  });

  reportCard.push(headerRow, dateRow, labelRow);

  subjects.forEach((subj) => {
    const row = [{ text: `${subj.name} (${subj.weightage}%)` }];
    terms.forEach((term) => {
      const g = studentGrades.find(
        (x) => x.SubjectId === subj.id && x.TermId === term.id
      );
      const obtained = g ? Number(g.ObtainedMarks) : 0;
      const weighted =
        g && g.totalMarks
          ? (obtained / g.totalMarks) * subj.weightage
          : 0;

      overallPercentage += weighted;
      row.push({ text: obtained.toFixed(1), alignment: "center" });
      row.push({ text: `${weighted.toFixed(1)}%`, alignment: "center" });
    });
    reportCard.push(row);
  });

  overallPercentage = Number(overallPercentage.toFixed(1));
  const gradeLetter = getGradeLetter(overallPercentage);
  const passFail = overallPercentage >= 60 ? "Pass" : "Fail";

  /* =========================
     7. Class Position + Percentile
  ========================= */
  let classPositionText = "N/A";
  let classPercentileText = "N/A";

  if (lastTerm) {
    const ranked = students
      .map((s) => {
        const sid = s.StudentId;
        const termGrades = allGrades.filter(
          (g) => g.StudentId === sid && g.TermId === lastTerm.id
        );
        if (!termGrades.length) return null;

        const total = termGrades.reduce((sum, g) => {
          const subj = subjects.find((x) => x.id === g.SubjectId);
          if (!subj || !g.totalMarks) return sum;
          return sum + (g.ObtainedMarks / g.totalMarks) * subj.weightage;
        }, 0);

        return { studentId: sid, total };
      })
      .filter(Boolean)
      .sort((a, b) => b.total - a.total);

    const idx = ranked.findIndex((r) => r.studentId === currentStudentId);
    if (idx >= 0) {
      const rank = idx + 1;
      classPositionText = formatOrdinal(rank);
      const below = ranked.length - rank;
      classPercentileText =
        ranked.length > 1
          ? `${Math.round((below / (ranked.length - 1)) * 100)}%`
          : "100%";
    }
  }

  /* =========================
     8. Final PDF Blocks
  ========================= */
  return [
    { text: "REPORT CARD", alignment: "center", fontSize: 22, bold: true },

    {
      columns: [
        {
          stack: [
            { text: currentBusiness.name, bold: true },
            { text: currentBusiness.address },
            { text: currentBusiness.phoneNumber },
            { text: currentBusiness.email },
          ],
        },
        {
          image: await logoToBase64(currentBusiness.imageUrl),
          width: 60,
          alignment: "right",
        },
      ],
    },

    {
      margin: [0, 10, 0, 10],
      columns: [
        {
          stack: [
            {
              text: [
                { text: "Name: " },
                { text: getStudentName(student), bold: true },
              ],
            },
            {
              text: [
                { text: "Overall Percentage: " },
                { text: `${overallPercentage}%`, bold: true },
              ],
            },
            {
              text: [
                { text: "Grade: " },
                { text: gradeLetter, bold: true },
              ],
            },
            {
              text: [
                { text: "Result: " },
                { text: passFail, bold: true },
              ],
            },
            {
              text: [
                { text: "Class Percentile: " },
                { text: classPercentileText, bold: true },
              ],
            },
          ],
        },
        { text: `Year: ${year.name}`, alignment: "center" },
        { text: `Class Position: ${classPositionText}`, alignment: "right" },
      ],
    },

    {
      table: {
        widths: "*",
        body: reportCard,
      },
      layout: {
        hLineWidth(i, node) {
          // top & bottom border
          return i === 0 || i === node.table.body.length ? 2 : 1;
        },
        vLineWidth(i, node) {
          // left & right border
          return i === 0 || i === node.table.widths.length ? 2 : 1;
        },
        hLineColor() {
          return "#000";
        },
        vLineColor() {
          return "#000";
        },
      },
    },

    ...(termCommentTable.length
      ? [{ table: { widths: "*", body: termCommentTable }, layout: "noBorders" }]
      : []),

    {
      columns: [
        { text: "Teacher Signature:\n\n\n__________________", alignment: "left" },
        { text: "Principal Signature:\n\n\n__________________", alignment: "right" },
      ],
      margin: [0, 30, 0, 0],
    },
  ];
}